import React from 'react';
import { getShopOptionCount } from '../config/runSettings';
import { GAME_CONFIG } from '../config/gameConfig';
import { SHOP_KINDS } from '../config/shopKinds';
import { normalizeActiveGameEffects } from '../engine/scoringEffects';
import {
  applyShopOption,
  drawShopOptions,
  getRoundTarget,
  getRoundTileTarget,
  isActStart,
  resolveShopOptionGrantedEffects,
} from '../engine/shop';
import { drawTiles } from '../engine/tileBag';
import { applyBoardModifierToSquare } from '../boardConfig';
import {
  trackShopReroll,
  trackShopSelection,
  trackShopSkip,
} from '../services/playerStatsTrackingService';
import {
  playModifierPlaceSound,
  triggerModifierPlaceHaptic,
} from '../services/audioService';
import {
  getRoundDiscardsWithEffects,
  getRoundSubmitsWithEffects,
  getShopRarityChancesWithEffects,
  sumActiveGameEffectValue,
} from './scrabbleGameHelpers';

const buildNextRoundState = (prevState, nextBagTiles) => {
  const nextRoundIndex = prevState.roundIndex + 1;
  const actBoundary = isActStart(nextRoundIndex);
  const bagWithCommitted = actBoundary
    ? [...nextBagTiles, ...Object.values(prevState.committedBoard)]
    : nextBagTiles;
  const nextRoundDraw = drawTiles(bagWithCommitted, GAME_CONFIG.rackSize);

  return {
    ...prevState,
    committedBoard: actBoundary ? {} : prevState.committedBoard,
    stagedBoard: {},
    rackTiles: nextRoundDraw.drawn,
    bagTiles: nextRoundDraw.bag,
    score: 0,
    history: [],
    tilesPlacedThisRound: 0,
    activeGameEffects: normalizeActiveGameEffects(prevState.activeGameEffects),
    gamePhase: 'playing',
    roundIndex: nextRoundIndex,
    roundTarget: getRoundTarget(nextRoundIndex),
    roundTileTarget: getRoundTileTarget(nextRoundIndex),
    submitsRemaining: getRoundSubmitsWithEffects(prevState.runSettings, prevState.activeGameEffects),
    discardsRemaining: getRoundDiscardsWithEffects(prevState.runSettings, prevState.activeGameEffects),
    isDiscardSelecting: false,
    discardSelectionTileIds: [],
    shopPicksRemaining: 0,
    shopRerollsRemaining: 0,
    shopOptions: [],
    selectedShopOptionId: null,
    pendingBoardPlacement: null,
  };
};

export const useShopLoop = ({ setState, setSubmitMessage }) => {
  const selectShopOption = React.useCallback((optionId) => {
    setState((prev) => {
      if (prev.gamePhase !== 'shop') {
        return prev;
      }

      const hasOption = prev.shopOptions.some((option) => option.id === optionId);
      if (!hasOption) {
        return prev;
      }

      return { ...prev, selectedShopOptionId: optionId };
    });
  }, [setState]);

  const confirmShopSelection = React.useCallback(() => {
    let selectionApplied = false;
    let selectedOptionData = null;
    let capturedAfterPickOptions = null;

    setState((prev) => {
      if (prev.gamePhase !== 'shop' || !prev.selectedShopOptionId) {
        return prev;
      }

      const selectedOption = prev.shopOptions.find((option) => option.id === prev.selectedShopOptionId);
      if (!selectedOption) {
        return prev;
      }

      if (selectedOption.kind === SHOP_KINDS.BOARD_MODIFIER_GRANT) {
        const heldId = `held-${selectedOption.id}-${Date.now()}`;
        const heldModifier = { ...selectedOption, heldId };
        const nextShopPicksRemaining = Math.max(0, Number(prev.shopPicksRemaining || 0) - 1);

        selectionApplied = true;
        selectedOptionData = {
          kind: selectedOption.kind,
          rarity: selectedOption.rarity,
          modifierId: selectedOption.modifierId ?? null,
          tileLetter: null,
          sourceTileId: null,
          tileModifiers: null,
          tileModifiersAfter: null,
          grantedEffects: [],
          shopPicksRemainingAfter: nextShopPicksRemaining,
        };

        const boardBase = {
          ...prev,
          heldBoardModifiers: [...prev.heldBoardModifiers, heldModifier],
          pendingBoardPlacement: null,
          selectedShopOptionId: null,
        };

        if (nextShopPicksRemaining > 0) {
          capturedAfterPickOptions = drawShopOptions({
            roundIndex: prev.roundIndex,
            bagTiles: prev.bagTiles,
            count: getShopOptionCount(prev.runSettings),
            rarityChances: getShopRarityChancesWithEffects(prev.runSettings, prev.activeGameEffects),
            runSettings: prev.runSettings,
          });
          return {
            ...boardBase,
            shopPicksRemaining: nextShopPicksRemaining,
            shopOptions: capturedAfterPickOptions,
          };
        }

        return buildNextRoundState(boardBase, prev.bagTiles);
      }

      const upgradedBagTiles = applyShopOption(prev.bagTiles, selectedOption);
      const upgradedTile = selectedOption.sourceTileId
        ? (upgradedBagTiles.find((t) => t.id === selectedOption.sourceTileId) ?? null)
        : null;
      const grantedShopEffects = resolveShopOptionGrantedEffects(selectedOption, {
        runSettings: prev.runSettings,
      });
      const previousShopRerollsBonus = sumActiveGameEffectValue(
        prev.activeGameEffects,
        'GLOBAL_SHOP_REROLLS_BONUS',
        'bonusRerolls'
      );
      const nextActiveGameEffects = normalizeActiveGameEffects([
        ...prev.activeGameEffects,
        ...grantedShopEffects,
      ]);
      const nextShopRerollsBonus = sumActiveGameEffectValue(
        nextActiveGameEffects,
        'GLOBAL_SHOP_REROLLS_BONUS',
        'bonusRerolls'
      );
      selectionApplied = true;

      const nextShopPicksRemaining = Math.max(0, Number(prev.shopPicksRemaining || 0) - 1);

      selectedOptionData = {
        kind: selectedOption.kind,
        rarity: selectedOption.rarity,
        modifierId: selectedOption.modifierId ?? null,
        tileLetter: selectedOption.tileLetter ?? null,
        sourceTileId: selectedOption.sourceTileId ?? null,
        tileModifiers: selectedOption.tileModifiers ?? null,
        tileModifiersAfter: upgradedTile ? upgradedTile.modifiers : null,
        grantedEffects: grantedShopEffects,
        shopPicksRemainingAfter: nextShopPicksRemaining,
      };

      const isGameModifier = selectedOption.kind === SHOP_KINDS.GAME_MODIFIER_GRANT;
      const nextOwnedGameModifiers = isGameModifier
        ? [...prev.ownedGameModifiers, selectedOption]
        : prev.ownedGameModifiers;

      if (nextShopPicksRemaining > 0) {
        const freshShopOptions = drawShopOptions({
          roundIndex: prev.roundIndex,
          bagTiles: upgradedBagTiles,
          count: getShopOptionCount(prev.runSettings),
          rarityChances: getShopRarityChancesWithEffects(prev.runSettings, nextActiveGameEffects),
          runSettings: prev.runSettings,
        });
        capturedAfterPickOptions = freshShopOptions;
        return {
          ...prev,
          bagTiles: upgradedBagTiles,
          activeGameEffects: nextActiveGameEffects,
          ownedGameModifiers: nextOwnedGameModifiers,
          shopPicksRemaining: nextShopPicksRemaining,
          shopRerollsRemaining: Math.max(
            0,
            Number(prev.shopRerollsRemaining || 0) + (nextShopRerollsBonus - previousShopRerollsBonus)
          ),
          shopOptions: freshShopOptions,
          selectedShopOptionId: null,
          pendingBoardPlacement: null,
        };
      }

      return buildNextRoundState(
        { ...prev, activeGameEffects: nextActiveGameEffects, ownedGameModifiers: nextOwnedGameModifiers },
        upgradedBagTiles
      );
    });

    if (selectionApplied) {
      trackShopSelection({ ...selectedOptionData, afterPickShopOptions: capturedAfterPickOptions });
      setSubmitMessage('Shop choice applied.');
      return true;
    }

    setSubmitMessage('Select one shop option first.');
    return false;
  }, [setState, setSubmitMessage]);

  const skipShopSelection = React.useCallback(() => {
    let skipApplied = false;
    let capturedSkipData = null;

    setState((prev) => {
      if (prev.gamePhase !== 'shop') {
        return prev;
      }

      skipApplied = true;

      const nextShopPicksRemaining = Math.max(0, Number(prev.shopPicksRemaining || 0) - 1);
      capturedSkipData = {
        roundIndex: prev.roundIndex,
        shopPicksRemaining: nextShopPicksRemaining,
        skippedOptions: prev.shopOptions,
      };

      if (nextShopPicksRemaining > 0) {
        return {
          ...prev,
          shopPicksRemaining: nextShopPicksRemaining,
          shopRerollsRemaining: prev.shopRerollsRemaining,
          shopOptions: drawShopOptions({
            roundIndex: prev.roundIndex,
            bagTiles: prev.bagTiles,
            count: getShopOptionCount(prev.runSettings),
            rarityChances: getShopRarityChancesWithEffects(prev.runSettings, prev.activeGameEffects),
            runSettings: prev.runSettings,
          }),
          selectedShopOptionId: null,
          pendingBoardPlacement: null,
        };
      }

      return buildNextRoundState(prev, prev.bagTiles);
    });

    if (skipApplied) {
      trackShopSkip(capturedSkipData);
      setSubmitMessage('Shop skipped. New round started.');
      return true;
    }

    setSubmitMessage('Skip is only available during shop phase.');
    return false;
  }, [setState, setSubmitMessage]);

  const rerollShopOptions = React.useCallback(() => {
    let rerolled = false;
    let capturedRerollData = null;

    setState((prev) => {
      if (prev.gamePhase !== 'shop' || Number(prev.shopRerollsRemaining || 0) <= 0) {
        return prev;
      }

      rerolled = true;
      const rerollsRemainingAfter = Math.max(0, Number(prev.shopRerollsRemaining || 0) - 1);
      const newShopOptions = drawShopOptions({
        roundIndex: prev.roundIndex,
        bagTiles: prev.bagTiles,
        count: getShopOptionCount(prev.runSettings),
        rarityChances: getShopRarityChancesWithEffects(prev.runSettings, prev.activeGameEffects),
        runSettings: prev.runSettings,
      });

      capturedRerollData = {
        rerollsRemainingAfter,
        oldOptions: prev.shopOptions,
        newOptions: newShopOptions,
      };

      return {
        ...prev,
        shopRerollsRemaining: rerollsRemainingAfter,
        shopOptions: newShopOptions,
        selectedShopOptionId: null,
        pendingBoardPlacement: null,
      };
    });

    if (!rerolled) {
      setSubmitMessage('No rerolls left this shop stage.');
      return false;
    }

    trackShopReroll(capturedRerollData);
    setSubmitMessage('Shop options rerolled.');
    return true;
  }, [setState, setSubmitMessage]);

  const startBoardModifierPlacement = React.useCallback((heldId) => {
    setState((prev) => {
      const held = prev.heldBoardModifiers.find((m) => m.heldId === heldId);
      if (!held) {
        return prev;
      }
      return { ...prev, pendingBoardPlacement: held };
    });
  }, [setState]);

  const placeBoardModifier = React.useCallback((squareTileId) => {
    let placed = false;
    let capturedSelectionData = null;

    setState((prev) => {
      if (!prev.pendingBoardPlacement) {
        return prev;
      }

      const occupiedByCommitted = Boolean(prev.committedBoard[squareTileId]);
      const occupiedByStaged = Boolean(prev.stagedBoard[squareTileId]);
      if (occupiedByCommitted || occupiedByStaged) {
        return prev;
      }

      const option = prev.pendingBoardPlacement;
      const { boardSquareModifierId, heldId } = option;
      if (!boardSquareModifierId) {
        return prev;
      }

      const nextBoardTiles = applyBoardModifierToSquare(prev.boardTiles, squareTileId, boardSquareModifierId);
      const nextBoardModifierOverrides = {
        ...prev.boardModifierOverrides,
        [squareTileId]: nextBoardTiles[squareTileId].modifiers,
      };

      placed = true;

      capturedSelectionData = {
        kind: option.kind,
        rarity: option.rarity,
        modifierId: option.modifierId,
        boardSquareModifierId,
        squareTileId,
      };

      return {
        ...prev,
        boardTiles: nextBoardTiles,
        boardModifierOverrides: nextBoardModifierOverrides,
        heldBoardModifiers: prev.heldBoardModifiers.filter((m) => m.heldId !== heldId),
        pendingBoardPlacement: null,
      };
    });

    if (placed) {
      trackShopSelection(capturedSelectionData);
      setSubmitMessage('Board square upgraded.');
      playModifierPlaceSound();
      triggerModifierPlaceHaptic();
      return true;
    }

    setSubmitMessage('That square already has a tile on it.');
    return false;
  }, [setState, setSubmitMessage]);

  const cancelBoardPlacement = React.useCallback(() => {
    setState((prev) => {
      if (!prev.pendingBoardPlacement) {
        return prev;
      }
      return { ...prev, pendingBoardPlacement: null };
    });
  }, [setState]);

  return {
    selectShopOption,
    confirmShopSelection,
    skipShopSelection,
    rerollShopOptions,
    startBoardModifierPlacement,
    placeBoardModifier,
    cancelBoardPlacement,
  };
};
